import type { ReactElement } from 'react';
import * as THREE from 'three';
import { CIVS } from '../gameplay/civilisations';
import { hasKind, kind } from '../gameplay/registry';
import { AnimMode, AnimatedInstances, createAnimatedMaterial, setMaterialSky } from '../engine/rendering/animatedMaterial';
import { fowUniforms } from '../engine/rendering/fowShader';
import { buildBuildingGeometry } from '../engine/rendering/models/buildingModels';
import { buildResourceGeometry } from '../engine/rendering/models/natureModels';
import { LIGHTING } from '../engine/rendering/sky';
import { buildUnitGeometry, civSkin } from '../engine/rendering/models/unitModels';

const PORTRAIT_PX = 192;
let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene;
let cam: THREE.PerspectiveCamera;
let mat: ReturnType<typeof createAnimatedMaterial>;
const cache = new Map<string, string>();

function init(): boolean {
  if (renderer) return true;
  try {
    // Portraits show at 84 CSS pixels: drawn at 192 so they stay sharp on high-density screens.
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = PORTRAIT_PX;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(1);
    renderer.setSize(PORTRAIT_PX, PORTRAIT_PX, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
  } catch {
    return false;
  }
  // Studio lighting: warm key, cool fill, and a back light that rims the silhouette against the
  // dark portrait frame. Metal parts reflect the sky.
  scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xdde8ff, 0x403020, 1.1));
  const key = new THREE.DirectionalLight(0xfff0d8, 2.6);
  key.position.set(-2, 3, 3);
  const fill = new THREE.DirectionalLight(0xb8c8ff, 0.6);
  fill.position.set(3, 1, 2);
  const back = new THREE.DirectionalLight(0xffe8c0, 2.2);
  back.position.set(1, 2.5, -3);
  scene.add(key, fill, back);
  cam = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  mat = createAnimatedMaterial({ surfaces: true, metals: true, groundAO: { height: 0.3, min: 0.7 }, rim: 0.18 });
  setMaterialSky(mat.material, LIGHTING.day);
  return true;
}

/**
 * Render a portrait of a unit/building/resource from its procedural model.
 * Returns a data URL (cached by key).
 */
export function portrait(key: string, civKey: string, color: number): string {
  const ck = `${key}|${civKey}|${color}`;
  const hit = cache.get(ck);
  if (hit) return hit;
  if (!hasKind(key) || !init() || !renderer) return '';
  const k = kind(key);
  const civ = CIVS.find((c) => c.key === civKey) ?? CIVS[0];
  let geo: THREE.BufferGeometry;
  let height = 1;
  if (k.unit) geo = buildUnitGeometry(k.unit, { accent: civ.uniform, skin: civSkin(civ.key) });
  else if (k.building) {
    const v = buildBuildingGeometry(k.building, civ, 2, 0, true);
    geo = v.geometry;
    height = v.height;
  } else geo = buildResourceGeometry(k.resource!.model, 0);
  const inst = new AnimatedInstances(geo, mat.material, null, 1, false);
  inst.begin();
  inst.push(0, 0, 0, k.building ? 0.6 : 0.5, 1, new THREE.Color(color), 0, AnimMode.Static, 0, height);
  inst.end();
  scene.add(inst.mesh);
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const size = bb.getSize(new THREE.Vector3());
  const centre = bb.getCenter(new THREE.Vector3());
  const r = Math.max(size.x, size.y, size.z);
  const focusY = k.unit && !k.unit.model.match(/cavalry|scout|horse|elephant|ram|mangonel|trebuchet|cannon|ship/) ? bb.max.y - r * 0.28 : centre.y;
  const dist = k.unit && focusY !== centre.y ? r * 1.3 : r * 2.2;
  cam.position.set(centre.x + dist * 0.35, focusY + dist * 0.3, centre.z + dist);
  cam.lookAt(centre.x, focusY, centre.z);
  const prevFow = fowUniforms.uFowEnabled.value;
  fowUniforms.uFowEnabled.value = 0;
  renderer.render(scene, cam);
  fowUniforms.uFowEnabled.value = prevFow;
  const url = renderer.domElement.toDataURL();
  scene.remove(inst.mesh);
  inst.mesh.dispose();
  geo.dispose();
  cache.set(ck, url);
  return url;
}

const P = (d: string) => <path d={d} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />;

/** Simple vector icons for actions, formations, stances and research. */
export function ActionIcon({ name }: { name: string }) {
  const icons: Record<string, ReactElement> = {
    stop: <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />,
    attackMove: <>{P('M4 20 L16 8')}{P('M12 4 L20 4 L20 12')}{P('M8 12 L12 16')}</>,
    patrol: <>{P('M5 8 H17 L14 5')}{P('M19 16 H7 L10 19')}</>,
    guard: <>{P('M12 3 L19 6 V12 C19 16 16 19 12 21 C8 19 5 16 5 12 V6 Z')}</>,
    retreat: <>{P('M20 12 H5')}{P('M10 6 L4 12 L10 18')}</>,
    follow: <>{P('M12 3 V7')}{P('M12 17 V21')}{P('M3 12 H7')}{P('M17 12 H21')}<circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /></>,
    delete: <>{P('M5 7 H19')}{P('M9 7 V4 H15 V7')}{P('M7 7 L8 20 H16 L17 7')}</>,
    build: <>{P('M4 20 L13 11')}{P('M11 5 L19 13 L16 16 L8 8 Z')}</>,
    back: <>{P('M15 5 L8 12 L15 19')}</>,
    rally: <>{P('M6 21 V3')}{P('M6 4 H18 L15 8 L18 12 H6')}</>,
    tech: <>{P('M7 4 H17 V20 H7 Z')}{P('M10 8 H14')}{P('M10 12 H14')}{P('M10 16 H13')}</>,
    buy: <>{P('M12 4 V20')}{P('M6 14 L12 20 L18 14')}</>,
    sell: <>{P('M12 20 V4')}{P('M6 10 L12 4 L18 10')}</>,
    garrison: <>{P('M4 11 L12 4 L20 11 V20 H4 Z')}{P('M12 9 V16')}{P('M9 13 L12 16 L15 13')}</>,
    unload: <>{P('M4 11 L12 4 L20 11 V20 H4 Z')}{P('M12 17 V10')}{P('M9 13 L12 10 L15 13')}</>,
    work: <>{P('M5 19 L15 9')}{P('M11 5 C14 3 18 4 20 7 C17 6 15 7 13 9 Z')}</>,
    bell: <>{P('M6 16 C6 9 8 6 12 6 C16 6 18 9 18 16 Z')}{P('M4 16 H20')}{P('M12 4 V6')}<circle cx="12" cy="19" r="1.6" fill="currentColor" /></>,
    repair: <>{P('M14 6 A4 4 0 1 0 18 10 L20 8 A6 6 0 0 1 12 14 L6 20 L4 18 L10 12 A6 6 0 0 1 16 4 Z')}</>,
    reseed: <>{P('M12 20 V10')}{P('M12 12 C8 12 6 9 6 6 C10 6 12 8 12 12')}{P('M12 14 C16 14 18 11 18 8 C14 8 12 10 12 14')}</>,
    'f-line': <>{[4, 8, 12, 16, 20].map((x) => <circle key={x} cx={x} cy="12" r="1.8" fill="currentColor" />)}</>,
    'f-column': <>{[4, 8, 12, 16, 20].map((y) => <circle key={y} cx="12" cy={y} r="1.8" fill="currentColor" />)}</>,
    'f-box': <>{[7, 12, 17].flatMap((x) => [7, 12, 17].map((y) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />))}</>,
    'f-wedge': <>{[[12, 5], [8, 11], [16, 11], [4, 17], [12, 17], [20, 17]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.8" fill="currentColor" />)}</>,
    'f-square': <>{P('M5 5 H19 V19 H5 Z')}{[[5, 5], [12, 5], [19, 5], [19, 12], [19, 19], [12, 19], [5, 19], [5, 12]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.8" fill="currentColor" />)}</>,
    'f-loose': <>{[[4, 6], [14, 4], [20, 12], [9, 13], [5, 20], [16, 19]].map(([x, y]) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />)}</>,
    's-aggressive': <>{P('M5 19 L19 5')}{P('M19 19 L5 5')}</>,
    's-defensive': <>{P('M12 3 L19 6 V12 C19 16 16 19 12 21 C8 19 5 16 5 12 V6 Z')}{P('M9 12 L11 14 L15 10')}</>,
    's-hold': <>{P('M8 4 V20')}{P('M16 4 V20')}</>,
    's-passive': <><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2.2" />{P('M8 12 H16')}</>,
  };
  const age = name.match(/^age(\d)$/);
  if (age) {
    return (
      <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9.5" fill="#6b5634" stroke="#f3d98b" strokeWidth="1.5" />
        <text x="12" y="16" textAnchor="middle" fontSize="10" fontFamily="Cinzel, serif" fontWeight="700" fill="#fff6dc">{['I', 'II', 'III', 'IV', 'V'][Number(age[1])]}</text></svg>
    );
  }
  return <svg viewBox="0 0 24 24" style={{ color: '#f3d98b' }}>{icons[name] ?? icons.tech}</svg>;
}

/** Small resource icons for the top bar and cost lines: food, wood, gold, stone, population. */
export function ResIcon({ r }: { r: number | 'pop' }) {
  const body: Record<string, ReactElement> = {
    // Food: a roast drumstick.
    0: <><path d="M14.5 3.5 C19 3 21.5 7 20 10.5 C18.8 13.4 15.5 14 13.6 13.2 L9.6 17.2 C10.4 18.3 10 19.9 8.8 20.4 C7.8 20.8 6.8 20.2 6.6 19.3 C5.6 19.5 4.6 18.8 4.7 17.7 C4.8 16.6 6.2 15.8 7.3 16.3 L11.3 12.3 C10.4 10.2 11 6.6 14.5 3.5 Z" fill="#d2603c" stroke="#5a2412" strokeWidth="1.1" /><path d="M14.5 5.5 C17 5.4 18.6 7.4 17.9 9.4" fill="none" stroke="#f4a07a" strokeWidth="1.2" strokeLinecap="round" /></>,
    // Wood: a log seen end on.
    1: <><rect x="3" y="8" width="15" height="9" rx="2" fill="#8a5a30" stroke="#3e260f" strokeWidth="1.1" /><ellipse cx="18" cy="12.5" rx="3.2" ry="4.5" fill="#d8a868" stroke="#3e260f" strokeWidth="1.1" /><ellipse cx="18" cy="12.5" rx="1.4" ry="2.1" fill="none" stroke="#8a5a30" strokeWidth="0.9" /><path d="M5 11 H13 M6 14 H12" stroke="#5e3a1a" strokeWidth="1" /></>,
    // Gold: a stack of coins.
    2: <><ellipse cx="12" cy="17" rx="7" ry="2.6" fill="#b8901a" stroke="#5a4208" strokeWidth="1" /><ellipse cx="12" cy="13.5" rx="7" ry="2.6" fill="#d8b030" stroke="#5a4208" strokeWidth="1" /><ellipse cx="12" cy="10" rx="7" ry="2.6" fill="#f6d860" stroke="#5a4208" strokeWidth="1" /><ellipse cx="12" cy="10" rx="3.4" ry="1.1" fill="none" stroke="#b8901a" strokeWidth="0.9" /></>,
    // Stone: a cut block with a rock beside it.
    3: <><path d="M4 18 L6 9 L13 7 L17 11 L15 19 Z" fill="#b8b4ac" stroke="#4a4740" strokeWidth="1.1" /><path d="M6 9 L10 12 L15 19 M10 12 L17 11" fill="none" stroke="#7a766e" strokeWidth="0.9" /><path d="M15 19 L17 14 L21 15 L20 19 Z" fill="#9a968e" stroke="#4a4740" strokeWidth="1" /></>,
    // Population: a person.
    pop: <><circle cx="12" cy="7" r="3.4" fill="#9ac8ff" stroke="#1e3e6a" strokeWidth="1.1" /><path d="M5 20 C5 14 8 12 12 12 C16 12 19 14 19 20 Z" fill="#5a8ad0" stroke="#1e3e6a" strokeWidth="1.1" /></>,
  };
  return <svg className="res-icon" viewBox="0 0 24 24" aria-hidden="true">{body[String(r)]}</svg>;
}
